import { ProgressSpinner } from "primereact/progressspinner";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import FormValidation from "../../../Common/FormValidation";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import { ChartOfAccountStyles } from "../Chart-Of-Account/ChartOfAccountShared";
import ProjectForm from "./ProjectForm";

const emptyInputs = {
  code: "",
  name: "",
  acc_customer_id: "",
  acc_cost_center_id: "",
  start_date: "",
  end_date: "",
  budget_amount: "",
  description: "",
  status: "active",
};

const Create = () => {
  const navigate = useNavigate();
  // The customer detail screen links here, so it can preselect its customer.
  const [searchParams] = useSearchParams();
  const [inputs, setInputs] = useState(emptyInputs);
  const [options, setOptions] = useState({
    customers: [],
    cost_centers: [],
    statuses: [],
  });
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedCostCenter, setSelectedCostCenter] = useState(null);
  const [error, setError] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      const customer_id = searchParams.get("customer_id") || "";

      try {
        const res = await Http.get(
          "/acc-project-form-options" +
            (customer_id ? "?customer_id=" + customer_id : ""),
        );
        setOptions(res.data);
        setInputs((values) => ({
          ...values,
          code: String(res.data.next_code ?? ""),
          acc_customer_id: customer_id,
        }));

        if (customer_id) {
          setSelectedCustomer(
            (res.data.customers || []).find(
              (option) => String(option.value) === String(customer_id),
            ) || null,
          );
        }
      } catch (e) {
        console.error("Fetch error:", e);
        setError({
          load: ["Could not load the form data. Please refresh and try again."],
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const selectHandleChange = (setSelected, field) => (selectedOption) => {
    setSelected(selectedOption);
    setInputs((values) => ({
      ...values,
      [field]: selectedOption ? selectedOption.value : "",
    }));
  };

  /**
   * Picking a customer narrows the cost center list to that customer's own
   * centers plus the internal ones, and clears a center that belonged to the
   * customer just replaced.
   */
  const customerHandleChange = (selectedOption) => {
    const customer_id = selectedOption ? selectedOption.value : "";

    setSelectedCustomer(selectedOption);
    setInputs((values) => ({
      ...values,
      acc_customer_id: customer_id,
      acc_cost_center_id: "",
    }));
    setSelectedCostCenter(null);

    Http.get(
      "/acc-project-form-options" +
        (customer_id ? "?customer_id=" + customer_id : ""),
    )
      .then((res) =>
        setOptions((values) => ({
          ...values,
          cost_centers: res.data.cost_centers || [],
        })),
      )
      .catch((e) => console.error("Fetch error:", e));
  };

  const validate = () => {
    const errors = {};

    if (!String(inputs.code).trim()) {
      errors.code = ["Code is required."];
    }
    if (!String(inputs.name).trim()) {
      errors.name = ["Name is required."];
    }
    if (
      inputs.start_date &&
      inputs.end_date &&
      inputs.end_date < inputs.start_date
    ) {
      errors.end_date = ["End date cannot be before the start date."];
    }

    setError(errors);
    return Object.keys(errors).length === 0;
  };

  const formSubmit = () => {
    if (saving || !validate()) {
      return;
    }

    setSaving(true);

    Http.post("/acc-project", {
      ...inputs,
      acc_customer_id: inputs.acc_customer_id || null,
      acc_cost_center_id: inputs.acc_cost_center_id || null,
      start_date: inputs.start_date || null,
      end_date: inputs.end_date || null,
      budget_amount: inputs.budget_amount === "" ? 0 : inputs.budget_amount,
    })
      .then((res) => {
        navigate("/acc-project", {
          state: {
            message: res.data.message,
            type: res.data.type || "success",
          },
        });
      })
      .catch((e) => {
        setError(
          e.response?.data?.errors || {
            general: [
              e.response?.data?.message ||
                "Could not save the project. Please try again.",
            ],
          },
        );
      })
      .finally(() => setSaving(false));
  };

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <ChartOfAccountStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">
              Create Project{" "}
              {inputs.code && <span className="coa-code">{inputs.code}</span>}
            </h5>
            <p className="text-muted mb-0 tx-12">
              A piece of work that costs can be charged to
            </p>
          </div>
          <Link to="/acc-project" className="btn btn-outline-secondary">
            <i className="fa fa-arrow-left mg-r-6"></i>Back to List
          </Link>
        </div>

        <div className="am-pagebody">
          <div className="row">
            <div className="col-lg">
              <FormValidation error={error} />
            </div>
          </div>

          {loading ? (
            <div className="coa-panel">
              <div className="coa-empty">
                <ProgressSpinner
                  style={{ width: "42px", height: "42px" }}
                  strokeWidth="6"
                />
              </div>
            </div>
          ) : (
            <ProjectForm
              inputs={inputs}
              options={options}
              selectedCustomer={selectedCustomer}
              selectedCostCenter={selectedCostCenter}
              onChange={handleChange}
              onCustomerChange={customerHandleChange}
              onCostCenterChange={selectHandleChange(
                setSelectedCostCenter,
                "acc_cost_center_id",
              )}
              onSubmit={formSubmit}
              saving={saving}
              submitLabel="Create Project"
              codeHint="Next free code. You may change it."
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Create;
